import { useCallback, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type {
  AdminClientSummary,
  CreateClientResponse,
  ListClientsResponse,
} from '@opsrev/shared';
import { createClient, listClients } from '../api';
import { useAsync } from '../useAsync';
import { relativeTime } from '../format';
import { EmptyState, ErrorState, Spinner } from '../components/Status';
import { Modal } from '../components/Modal';
import { EnrollmentInstructions } from '../components/EnrollmentInstructions';

function ClientRow({ c }: { c: AdminClientSummary }): ReactElement {
  return (
    <tr>
      <td>
        <Link to={`/clients/${c.id}`} className="row-link">
          {c.name}
        </Link>
      </td>
      <td className="num">{c.deviceCount}</td>
      <td className="num">{c.sessionCount}</td>
      <td>{relativeTime(c.lastSeenAt)}</td>
      <td>
        <span className={`badge client-${c.status}`}>
          {c.status === 'active' ? 'Active' : 'Disabled'}
        </span>
      </td>
    </tr>
  );
}

function NewClientFlow({
  onCreated,
}: {
  onCreated: (resp: CreateClientResponse) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateClientResponse | null>(null);

  const reset = () => {
    setOpen(false);
    setName('');
    setSubmitting(false);
    setError(null);
    setCreated(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await createClient({ name: name.trim() });
      setCreated(resp);
      onCreated(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button className="btn primary" onClick={() => setOpen(true)}>
        New client
      </button>
      {open ? (
        <Modal
          title={created ? `Enroll “${created.client.name}”` : 'New client'}
          onClose={reset}
          width={created ? 620 : 460}
        >
          {created ? (
            <>
              <EnrollmentInstructions
                clientName={created.client.name}
                enrollmentToken={created.enrollment.enrollmentToken}
              />
              <div className="modal-actions">
                <Link className="btn" to={`/clients/${created.client.id}`} onClick={reset}>
                  Open client
                </Link>
                <button className="btn primary" onClick={reset}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={submit}>
              <label className="field">
                <span>Client name</span>
                <input
                  autoFocus
                  type="text"
                  value={name}
                  placeholder="Acme Revenue Ops"
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              {error ? <p className="error-text">{error}</p> : null}
              <div className="modal-actions">
                <button type="button" className="btn" onClick={reset}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={submitting || !name.trim()}
                >
                  {submitting ? 'Creating…' : 'Create client'}
                </button>
              </div>
            </form>
          )}
        </Modal>
      ) : null}
    </>
  );
}

export function Dashboard(): ReactElement {
  const loader = useCallback(() => listClients(), []);
  const { data, loading, error, reload } = useAsync<ListClientsResponse>(loader, []);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Clients</h1>
          <p className="muted">Enrolled organizations running the capture app.</p>
        </div>
        <NewClientFlow onCreated={() => reload()} />
      </div>

      {loading && !data ? <Spinner label="Loading clients…" /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data ? (
        data.clients.length === 0 ? (
          <EmptyState
            title="No clients yet"
            hint="Create your first client to generate an enrollment token."
          />
        ) : (
          <div className="card table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="num">Devices</th>
                  <th className="num">Sessions</th>
                  <th>Last activity</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c) => (
                  <ClientRow key={c.id} c={c} />
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}
